<template>
  <div class="invoices-tab">
    <!-- Invoices Section -->
    <div class="invoices-section">
      <div class="section-header">
        <h3 class="section-title">{{ $t("globals.invoices") }}</h3>
      </div>

      <div class="table-wrapper">
        <table class="modern-table invoices-table">
          <thead>
            <tr>
              <th @click="sortInvoices('id')" class="sortable">
                ID
                <span class="sort-icon" v-if="invoicesTable.sortBy === 'id'">
                  {{ invoicesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th>{{ $t("globals.invoice_no") }}</th>
              <th @click="sortInvoices('is_paid')" class="sortable">
                {{ $t("globals.paid") }}
                <span class="sort-icon" v-if="invoicesTable.sortBy === 'is_paid'">
                  {{ invoicesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortInvoices('created_at')" class="sortable">
                {{ $t("globals.created_at") }}
                <span class="sort-icon" v-if="invoicesTable.sortBy === 'created_at'">
                  {{ invoicesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortInvoices('sent_at')" class="sortable">
                {{ $t("globals.sent_at") }}
                <span class="sort-icon" v-if="invoicesTable.sortBy === 'sent_at'">
                  {{ invoicesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th>{{ $t("globals.sent_to") }}</th>
              <th class="text-right">{{ $t("globals.total_price") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invoice in paginatedInvoices"
              :key="invoice.id"
              @click="toInvoice(invoice)"
              class="clickable-row"
            >
              <td>{{ invoice.id }}</td>
              <td>
                <a
                  :href="`${getBackendUrl}/pdf_invoices/${invoice.id}.pdf?tmpl=0&locale=${getLocale}`"
                  target="_blank"
                  class="invoice-link"
                  @click.stop
                >
                  {{ invoice.invoice_no }}
                </a>
              </td>
              <td>
                <span class="paid-status">
                  <v-icon v-if="invoice.is_paid" name="regular/check-square" class="paid-icon"></v-icon>
                  <v-icon v-else name="regular/square" class="unpaid-icon"></v-icon>
                </span>
              </td>
              <td :title="invoice.created_at">
                {{ invoice.created_at ? invoice.created_at.substring(0, 10) : '' }}
              </td>
              <td :title="invoice.sent_at || ''">
                {{ invoice.sent_at ? invoice.sent_at.substring(0, 10) : '' }}
              </td>
              <td>{{ invoice.sent_to }}</td>
              <td class="text-right">{{ currencyFormat(invoice.total_price, currency) }}</td>
            </tr>
            <tr v-if="!invoices || invoices.length === 0">
              <td colspan="7" class="empty-state-cell">{{ $t("globals.no_invoices_found") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="invoices && invoices.length > 0" class="pagination-section">
        <div class="controls-left">
          <span class="summary-label">
            {{ $t("globals.showing") }} {{ ((invoicesTable.currentPage - 1) * invoicesTable.perPage) + 1 }}-{{ Math.min(invoicesTable.currentPage * invoicesTable.perPage, invoices.length) }}
          </span>
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            @click="invoicesTable.currentPage--"
            :disabled="invoicesTable.currentPage === 1"
          >
            &lt;
          </button>
          <button
            class="page-btn"
            @click="invoicesTable.currentPage++"
            :disabled="invoicesTable.currentPage === invoiceTotalPages"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>

    <!-- Scheduled Invoices Section -->
    <div class="scheduled-invoices-section">
      <div class="section-header">
        <h3 class="section-title">{{ $t("globals.scheduled_invoices") }}</h3>
      </div>

      <div class="table-wrapper">
        <table class="modern-table scheduled-invoices-table">
          <thead>
            <tr>
              <th @click="sortScheduled('id')" class="sortable">
                ID
                <span class="sort-icon" v-if="invoiceSchedulesTable.sortBy === 'id'">
                  {{ invoiceSchedulesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortScheduled('status')" class="sortable">
                {{ $t("globals.status") }}
                <span class="sort-icon" v-if="invoiceSchedulesTable.sortBy === 'status'">
                  {{ invoiceSchedulesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortScheduled('total_price')" class="sortable text-right">
                {{ $t("globals.total_price") }}
                <span class="sort-icon" v-if="invoiceSchedulesTable.sortBy === 'total_price'">
                  {{ invoiceSchedulesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th @click="sortScheduled('schedule_at')" class="sortable">
                {{ $t("globals.schedule_at") }}
                <span class="sort-icon" v-if="invoiceSchedulesTable.sortBy === 'schedule_at'">
                  {{ invoiceSchedulesTable.sortDesc ? '▼' : '▲' }}
                </span>
              </th>
              <th class="text-center">{{ $t("globals.actions") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="schedule in paginatedSchedules" :key="schedule.id">
              <td>{{ schedule.id }}</td>
              <td>
                <span class="status-badge" :class="'status-' + schedule.status.toLowerCase().replace('_', '-')">
                  {{ schedule.status }}
                </span>
              </td>
              <td class="text-right">{{ currencyFormat(schedule.total_price, currency) }}</td>
              <td>{{ schedule.schedule_at ? schedule.schedule_at.substring(0, 10) : '' }}</td>
              <td class="text-center">
                <button
                  v-if="schedule.status === 'scheduled' || schedule.status === 'scheduled_plus'"
                  class="action-btn-lg pause-btn"
                  @click="pauseScheduledInvoice(schedule.id)"
                  title="Pause scheduled invoice"
                >
                  <v-icon name="pause"></v-icon>
                </button>
                <button
                  v-if="schedule.status === 'paused' || schedule.status === 'paused_plus'"
                  class="action-btn-lg play-btn"
                  @click="continueScheduledInvoice(schedule.id)"
                  title="Resume scheduled invoice"
                >
                  <v-icon name="play"></v-icon>
                </button>
              </td>
            </tr>
            <tr v-if="!invoiceSchedules || invoiceSchedules.length === 0">
              <td colspan="5" class="empty-state-cell">{{ $t("globals.no_scheduled_invoices_found") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="invoiceSchedules && invoiceSchedules.length > 0" class="pagination-section">
        <div class="controls-left">
          <span class="summary-label">
            {{ $t("globals.showing") }} {{ ((invoiceSchedulesTable.currentPage - 1) * invoiceSchedulesTable.perPage) + 1 }}-{{ Math.min(invoiceSchedulesTable.currentPage * invoiceSchedulesTable.perPage, invoiceSchedules.length) }}
          </span>
        </div>
        <div class="pagination-controls">
          <button
            class="page-btn"
            @click="invoiceSchedulesTable.currentPage--"
            :disabled="invoiceSchedulesTable.currentPage === 1"
          >
            &lt;
          </button>
          <button
            class="page-btn"
            @click="invoiceSchedulesTable.currentPage++"
            :disabled="invoiceSchedulesTable.currentPage === schedulesTotalPages"
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { currencyFormat } from "@/helpers/price";
import { mapGetters, mapActions } from "vuex";

export default {
  props: {
    refreshOrder: Function,
    currency: String,
    invoices: Array,
    invoiceSchedules: Array
  },
  data() {
    return {
      invoicesTable: {
        sortBy: "sent_at",
        sortDesc: true,
        perPage: 5,
        currentPage: 1,
        fields: [
          {
            key: "id",
            sortable: true
          },
          {
            key: "invoice_no",
            label: this.$t("globals.invoice_no")
          },
          {
            key: "is_paid",
            label: this.$t("globals.paid"),
            sortable: true
          },
          {
            key: "created_at",
            label: this.$t("globals.created_at"),
            sortable: true
          },
          {
            key: "sent_at",
            label: this.$t("globals.sent_at"),
            sortable: true
          },
          {
            key: "sent_to",
            label: this.$t("globals.sent_to")
          },
          {
            key: "total_price",
            label: this.$t("globals.total_price")
          }
        ]
      },
      invoiceSchedulesTable: {
        sortBy: "schedule_at",
        sortDesc: false,
        perPage: 5,
        currentPage: 1,
        fields: [
          {
            key: "id",
            sortable: true
          },
          {
            key: "status",
            label: this.$t("globals.status"),
            sortable: true
          },
          {
            key: "total_price",
            label: this.$t("globals.total_price"),
            sortable: true
          },
          {
            key: "schedule_at",
            label: this.$t("globals.schedule_at"),
            sortable: true
          },
          {
            key: "actions",
            label: this.$t("globals.actions")
          }
        ]
      }
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    currencyFormat,
    toInvoice(item) {
      this.$router.push({ name: "invoice", params: { id: item.id } });
    },
    sortInvoices(field) {
      if (this.invoicesTable.sortBy === field) {
        this.invoicesTable.sortDesc = !this.invoicesTable.sortDesc;
      } else {
        this.invoicesTable.sortBy = field;
        this.invoicesTable.sortDesc = false;
      }
    },
    sortScheduled(field) {
      if (this.invoiceSchedulesTable.sortBy === field) {
        this.invoiceSchedulesTable.sortDesc = !this.invoiceSchedulesTable.sortDesc;
      } else {
        this.invoiceSchedulesTable.sortBy = field;
        this.invoiceSchedulesTable.sortDesc = false;
      }
    },
    pauseScheduledInvoice(id) {
      let data = {
        query: `
          mutation {
            res: InvoiceScheduledPause (
              invoice_id: ${parseInt(id)}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refreshOrder();
      });
    },
    continueScheduledInvoice(id) {
      let data = {
        query: `
          mutation {
            res: InvoiceScheduledContinue (
              invoice_id: ${parseInt(id)}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refreshOrder();
      });
    }
  },
  computed: {
    ...mapGetters(["getLocale", "getBackendUrl"]),
    sortedInvoices() {
      if (!this.invoices || this.invoices.length === 0) return [];

      const sorted = [...this.invoices].sort((a, b) => {
        const field = this.invoicesTable.sortBy;
        let aVal = a[field];
        let bVal = b[field];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.invoicesTable.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.invoicesTable.sortDesc ? -1 : 1;
        return 0;
      });

      return sorted;
    },
    paginatedInvoices() {
      const start = (this.invoicesTable.currentPage - 1) * this.invoicesTable.perPage;
      const end = start + this.invoicesTable.perPage;
      return this.sortedInvoices.slice(start, end);
    },
    invoiceTotalPages() {
      return Math.ceil((this.invoices?.length || 0) / this.invoicesTable.perPage);
    },
    sortedSchedules() {
      if (!this.invoiceSchedules || this.invoiceSchedules.length === 0) return [];

      const sorted = [...this.invoiceSchedules].sort((a, b) => {
        const field = this.invoiceSchedulesTable.sortBy;
        let aVal = a[field];
        let bVal = b[field];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (aVal < bVal) return this.invoiceSchedulesTable.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.invoiceSchedulesTable.sortDesc ? -1 : 1;
        return 0;
      });

      return sorted;
    },
    paginatedSchedules() {
      const start = (this.invoiceSchedulesTable.currentPage - 1) * this.invoiceSchedulesTable.perPage;
      const end = start + this.invoiceSchedulesTable.perPage;
      return this.sortedSchedules.slice(start, end);
    },
    schedulesTotalPages() {
      return Math.ceil((this.invoiceSchedules?.length || 0) / this.invoiceSchedulesTable.perPage);
    }
  }
};
</script>

<style scoped>
.invoices-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Invoices Section - match devices tab (no padding) */
.invoices-section {
  padding: 0;
  background: transparent;
}

/* Scheduled Invoices Section - match devices tab (no padding) */
.scheduled-invoices-section {
  padding: 0;
  background: transparent;
}

/* Override hover for clickable rows */
.modern-table tbody tr.clickable-row {
  cursor: pointer;
}
</style>
